import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import ActionButtons from '../ActionButtons';
import DefineColumns from './DefineColumns';
import { filterOcplc, loadCountForDT, ocplcTableData, refreshOcplc } from 'actions/ocplcAction';
import { dataGridStyles, container } from "../DashboardHelpers";
import CustomPagination from './CustomPagination';
import { getRowClassName, pageSizeOptions } from './ocplcHelpers';
import { snackBar } from 'actions/modelActions';
import { useNavigate } from 'react-router';
import { DASHBOARD_DT } from 'constants/ocplcConstants';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { LOCK_STATUS_DASHBOARD } from 'constants/ocplcConstants';
import { DEFAULT_PAGE_SIZE } from 'constants/configConstants';
import { saveToLocalStorage, SELECTED_ROW } from 'utils/localStorageUtil';

const OcplcDashboard = ({ auth }) => { 
    const userRole = auth?.ocplcRole;
    const userEmail = auth?.email;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        filteredOcplcData = [],
        filters = [],
        isLoading,
        ocplcProductLines = [],
        statuses = [],
        tableData,
        lockStatus = {},
        ocplcProductModels = []
    } = useSelector(state => state.ocplc);

    const [checkedDashboardRows, setCheckedDashboardRows] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);

    useEffect(() => {
        dispatch(refreshOcplc());
        dispatch(ocplcTableData());
        dispatch(snackBar(false));
        dispatch(loadCountForDT(DASHBOARD_DT));
    }, []);

    const handleChange = useCallback((col, value) => {
        dispatch(filterOcplc({ ...col, searchText: value }))
    }, []);

    const handleRowClick = (params, event) => {
        const { kmatId, productLineID, productGroup } = params.row;
        if (event.target.type !== "checkbox") {
            event.defaultMuiPrevented = true;
        }
        if (event.target.type !== 'checkbox' && !event.target.closest('.action-button-class')) {
            if (!kmatId || !productLineID || !productGroup) {
                dispatch(snackBar(true, `Product Line is empty for KMAT - ${kmatId}`, 'error'));
            } else {
                localStorage.setItem('ocplc_source', 'viewRecord');
                saveToLocalStorage(SELECTED_ROW, params.row);
                navigate(`/ocplc/viewRecord`,  { state: { source: 'viewRecord' } } );
            }
        }
    };

    const columns = useMemo(() => DefineColumns({
        filters, handleChange, ocplcProductLines, statuses, userEmail, userRole
    }), [filters, statuses]);

    // Keep checkedDashboardRows in sync with filteredOcplcData and selectedIds
    useEffect(() => {
        if (!ocplcProductModels || ocplcProductModels.length === 0) {
            setCheckedDashboardRows([]);
            return;
        }
            const dataMap = new Map(ocplcProductModels.map(row => [row.kmatId, row]));
            const selectedItems = selectedIds.map(id => dataMap.get(id)).filter(Boolean);
            setCheckedDashboardRows(selectedItems);
    }, [ocplcProductModels, selectedIds]);
 
    const handleRowSelection = useCallback((newSelection) => {
        setSelectedIds((prevSelected) => {
            const visibleIds = filteredOcplcData.map(row => row.kmatId);
            const uncheckedVisibleIds = visibleIds.filter(id => !newSelection.includes(id));
            const merged = new Set(prevSelected);
            newSelection.forEach(id => merged.add(id));
            uncheckedVisibleIds.forEach(id => merged.delete(id));
            return Array.from(merged);
        });
    }, [filteredOcplcData]);

    const handleClose = useCallback(() => {
        dispatch({ type: LOCK_STATUS_DASHBOARD, payload: {} });
    }, []);

    return (
        <>
            <Box sx={{ ...container, minWidth: '100%' }}>
                <ActionButtons
                    title={"OCPLC Dashboard"}
                    tableData={tableData}
                    userRole={userRole}
                    userEmail={userEmail}
                    checkedDashboardRows={checkedDashboardRows}
                    setCheckedDashboardRows={setCheckedDashboardRows}
                    setSelectedIds={setSelectedIds}
                    selectedIds={selectedIds}
                />               
                <DataGrid
                    columns={columns}
                    rows={filteredOcplcData}
                    getRowId={(row) => row.kmatId}
                    loading={isLoading}
                    rowHeight={48}
                    columnHeaderHeight={90}
                    className='dashboard-data-grid'
                    slots={{ 
                        pagination: (props) => <CustomPagination {...props} pageSizeOptions={pageSizeOptions} />,
                     }}
                    disableSelectionOnClick
                    getRowClassName={getRowClassName}
                    headerClassName='dashboard-grid-headers'
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE },
                        },
                    }}
                    sx={dataGridStyles}
                    disableColumnFilter
                    disableColumnMenu
                    hideFooterSelectedRowCount={true}
                    checkboxSelection
                    rowSelectionModel={selectedIds}
                    onRowSelectionModelChange={handleRowSelection}
                    onRowClick={handleRowClick}
                />
            </Box>

            {lockStatus.isError && (
                <ConfirmationDialog
                    open={lockStatus.isError}
                    onClose={handleClose}
                    content={lockStatus.response}
                />
            )}
        </>
    );
};

export default OcplcDashboard;
